import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import * as XLSX from 'xlsx';
import { createClient } from '../services/clients';
import { createAuditLog } from '../services/audit';
import { getExcelSpreadsheetData, saveExcelSpreadsheetData } from '../services/excelSpreadsheet';
import './ExcelSpreadsheet.css';

const columnHelper = createColumnHelper();
const STORAGE_KEY_CLIENTS = 'excel_spreadsheet_clients_data';
const STORAGE_KEY_TAGS = 'excel_spreadsheet_tags_data';

export default function ExcelSpreadsheet({ onCreateClient, defaultTab = 'clients' }) {
  const activeTab = defaultTab; // 'clients' or 'tags'
  
  // Client list state
  const [clientData, setClientData] = useState([]);
  const [clientColumns, setClientColumns] = useState([]);
  const [clientFileName, setClientFileName] = useState('');
  const [clientLoading, setClientLoading] = useState(false);
  
  // Tag list state
  const [tagData, setTagData] = useState([]);
  const [tagColumns, setTagColumns] = useState([]);
  const [tagFileName, setTagFileName] = useState('');
  const [tagLoading, setTagLoading] = useState(false);
  
  const fileInputRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const handleCreateClient = useCallback(async (rowData) => {
    // Map Excel columns to client fields (case-insensitive matching)
    const mapField = (excelKey) => {
      const keyLower = excelKey.toLowerCase();
      for (const [rowKey, value] of Object.entries(rowData)) {
        if (rowKey.toLowerCase() === keyLower) {
          return String(value || '').trim();
        }
      }
      return '';
    };

    const clientDataObj = {
      name: mapField('name') || mapField('Name') || mapField('NAME') || '',
      email: mapField('email') || mapField('Email') || mapField('EMAIL') || '',
      phone: mapField('phone') || mapField('Phone') || mapField('PHONE') || '',
      address: mapField('address') || mapField('Address') || mapField('ADDRESS') || '',
      city: mapField('city') || mapField('City') || mapField('CITY') || '',
      state: mapField('state') || mapField('State') || mapField('STATE') || '',
      zip: mapField('zip') || mapField('ZIP') || mapField('Zip') || 
           mapField('zipcode') || mapField('ZipCode') || mapField('ZIPCODE') || '',
      source: mapField('source') || mapField('Source') || mapField('SOURCE') || '',
      notes: mapField('notes') || mapField('Notes') || mapField('NOTES') || '',
    };

    if (!clientDataObj.name) {
      alert('Cannot create client: Name field is required. Please ensure your Excel file has a "Name" column.');
      return;
    }

    if (window.confirm(`Create a new client from this row?\n\nName: ${clientDataObj.name}\nEmail: ${clientDataObj.email || 'N/A'}\nPhone: ${clientDataObj.phone || 'N/A'}`)) {
      try {
        const newClient = await createClient(clientDataObj);
        
        await createAuditLog('create', 'client', newClient.id, {
          client_name: newClient.name,
          source: 'excel_spreadsheet',
          excel_file: clientFileName || ''
        });

        if (onCreateClient) {
          onCreateClient(newClient);
        }

        alert('Client created successfully!');
      } catch (error) {
        alert('Failed to create client: ' + error.message);
      }
    }
  }, [clientFileName, onCreateClient]);

  // Helper function to generate columns (with or without create button)
  const generateColumns = useCallback((data, includeCreateButton = true) => {
    if (!data || data.length === 0) return [];
    
    const firstRow = data[0];
    const generatedColumns = Object.keys(firstRow).map((key) => 
      columnHelper.accessor(key, {
        header: key,
        cell: info => info.getValue(),
        enableSorting: true,
      })
    );

    // Add actions column only if includeCreateButton is true
    if (includeCreateButton) {
      generatedColumns.push(
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => (
            <button
              className="btn-create-from-row"
              onClick={() => handleCreateClient(row.original)}
              title="Create Client from this row"
            >
              Create Client
            </button>
          ),
        })
      );
    }

    return generatedColumns;
  }, [handleCreateClient]);

  // Load saved client data from database only when this tab is active (avoids slow double-fetch)
  useEffect(() => {
    if (defaultTab !== 'clients') return;

    const loadClientData = async () => {
      setClientLoading(true);
      try {
        const savedData = await getExcelSpreadsheetData('clients');
        if (savedData && savedData.excelData && savedData.excelData.length > 0) {
          setClientData(savedData.excelData);
          setClientFileName(savedData.fileName || '');
          const generatedColumns = generateColumns(savedData.excelData, true);
          if (generatedColumns.length > 0) {
            setClientColumns(generatedColumns);
          }
        }
      } catch (error) {
        console.error('Error loading saved client Excel data from database:', error);
        try {
          const localData = localStorage.getItem(STORAGE_KEY_CLIENTS);
          if (localData) {
            const parsed = JSON.parse(localData);
            if (parsed.excelData && parsed.excelData.length > 0) {
              setClientData(parsed.excelData);
              setClientFileName(parsed.fileName || '');
              const generatedColumns = generateColumns(parsed.excelData, true);
              if (generatedColumns.length > 0) {
                setClientColumns(generatedColumns);
              }
            }
          }
        } catch (localError) {
          console.error('Error loading from localStorage:', localError);
        }
      } finally {
        setClientLoading(false);
      }
    };

    loadClientData();
  }, [defaultTab, generateColumns]);

  // Load saved tag data from database only when this tab is active
  useEffect(() => {
    if (defaultTab !== 'tags') return;

    const loadTagData = async () => {
      setTagLoading(true);
      try {
        const savedData = await getExcelSpreadsheetData('tags');
        if (savedData && savedData.excelData && savedData.excelData.length > 0) {
          setTagData(savedData.excelData);
          setTagFileName(savedData.fileName || '');
          const generatedColumns = generateColumns(savedData.excelData, false);
          if (generatedColumns.length > 0) {
            setTagColumns(generatedColumns);
          }
        }
      } catch (error) {
        console.error('Error loading saved tag Excel data from database:', error);
        try {
          const localData = localStorage.getItem(STORAGE_KEY_TAGS);
          if (localData) {
            const parsed = JSON.parse(localData);
            if (parsed.excelData && parsed.excelData.length > 0) {
              setTagData(parsed.excelData);
              setTagFileName(parsed.fileName || '');
              const generatedColumns = generateColumns(parsed.excelData, false);
              if (generatedColumns.length > 0) {
                setTagColumns(generatedColumns);
              }
            }
          }
        } catch (localError) {
          console.error('Error loading from localStorage:', localError);
        }
      } finally {
        setTagLoading(false);
      }
    };

    loadTagData();
  }, [defaultTab, generateColumns]);

  // Save client data to database whenever it changes (debounced)
  useEffect(() => {
    if (clientData.length > 0 && clientColumns.length > 0) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce save operation (wait 2 seconds after last change)
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await saveExcelSpreadsheetData('clients', clientData, clientFileName, clientColumns);
          // Also save to localStorage as backup
          try {
            const dataToStore = {
              excelData: clientData,
              fileName: clientFileName,
            };
            const jsonString = JSON.stringify(dataToStore);
            if (jsonString.length <= 4 * 1024 * 1024) { // 4MB limit
              localStorage.setItem(STORAGE_KEY_CLIENTS, jsonString);
            }
          } catch (localError) {
            // Ignore localStorage errors, database is primary
            console.warn('Could not save to localStorage:', localError);
          }
        } catch (error) {
          console.error('Error saving client Excel data to database:', error);
          // Fallback to localStorage if database fails
          try {
            const dataToStore = {
              excelData: clientData,
              fileName: clientFileName,
            };
            const jsonString = JSON.stringify(dataToStore);
            if (jsonString.length <= 4 * 1024 * 1024) {
              localStorage.setItem(STORAGE_KEY_CLIENTS, jsonString);
            }
          } catch (localError) {
            console.error('Could not save to localStorage:', localError);
          }
        }
      }, 2000); // 2 second debounce

      // Cleanup timeout on unmount
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    }
  }, [clientData, clientFileName, clientColumns]);

  // Save tag data to database whenever it changes (debounced)
  useEffect(() => {
    if (tagData.length > 0 && tagColumns.length > 0) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce save operation (wait 2 seconds after last change)
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await saveExcelSpreadsheetData('tags', tagData, tagFileName, tagColumns);
          // Also save to localStorage as backup
          try {
            const dataToStore = {
              excelData: tagData,
              fileName: tagFileName,
            };
            const jsonString = JSON.stringify(dataToStore);
            if (jsonString.length <= 4 * 1024 * 1024) { // 4MB limit
              localStorage.setItem(STORAGE_KEY_TAGS, jsonString);
            }
          } catch (localError) {
            // Ignore localStorage errors, database is primary
            console.warn('Could not save to localStorage:', localError);
          }
        } catch (error) {
          console.error('Error saving tag Excel data to database:', error);
          // Fallback to localStorage if database fails
          try {
            const dataToStore = {
              excelData: tagData,
              fileName: tagFileName,
            };
            const jsonString = JSON.stringify(dataToStore);
            if (jsonString.length <= 4 * 1024 * 1024) {
              localStorage.setItem(STORAGE_KEY_TAGS, jsonString);
            }
          } catch (localError) {
            console.error('Could not save to localStorage:', localError);
          }
        }
      }, 2000); // 2 second debounce

      // Cleanup timeout on unmount
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    }
  }, [tagData, tagFileName, tagColumns]);

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 10MB to prevent memory issues)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      alert(`File is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum file size is 10MB. Please use a smaller file or split it into multiple files.`);
      return;
    }

    const isClientTab = activeTab === 'clients';
    
    if (isClientTab) {
      setClientLoading(true);
      setClientFileName(file.name);
    } else {
      setTagLoading(true);
      setTagFileName(file.name);
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with row limit to prevent memory issues
        const MAX_ROWS = 50000; // Limit to 50k rows
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          defval: '', // Default value for empty cells
          raw: false // Convert all values to strings
        });

        if (jsonData.length === 0) {
          alert('The Excel file appears to be empty.');
          if (isClientTab) {
            setClientLoading(false);
          } else {
            setTagLoading(false);
          }
          return;
        }

        // Warn if file is large
        if (jsonData.length > 10000) {
          const proceed = window.confirm(
            `This file contains ${jsonData.length.toLocaleString()} rows. ` +
            `Loading large files may cause performance issues. ` +
            `Only the first ${MAX_ROWS.toLocaleString()} rows will be loaded. Continue?`
          );
          if (!proceed) {
            if (isClientTab) {
              setClientLoading(false);
            } else {
              setTagLoading(false);
            }
            return;
          }
        }

        // Limit rows to prevent memory issues
        const limitedData = jsonData.slice(0, MAX_ROWS);
        if (jsonData.length > MAX_ROWS) {
          alert(`File contains ${jsonData.length.toLocaleString()} rows. Only the first ${MAX_ROWS.toLocaleString()} rows have been loaded.`);
        }

        // Generate columns from the data
        const generatedColumns = generateColumns(limitedData, isClientTab);
        
        if (isClientTab) {
          setClientColumns(generatedColumns);
          setClientData(limitedData);
          setClientLoading(false);
          // Save to database (will be handled by useEffect, but we trigger it here)
        } else {
          setTagColumns(generatedColumns);
          setTagData(limitedData);
          setTagLoading(false);
          // Save to database (will be handled by useEffect, but we trigger it here)
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        if (error.message && error.message.includes('memory')) {
          alert('File is too large to process. Please use a smaller file or split it into multiple files.');
        } else {
          alert('Failed to parse Excel file: ' + error.message);
        }
        if (isClientTab) {
          setClientLoading(false);
        } else {
          setTagLoading(false);
        }
      }
    };

    reader.onerror = () => {
      alert('Failed to read file');
      if (isClientTab) {
        setClientLoading(false);
      } else {
        setTagLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Get current tab's data and columns (memoized to prevent unnecessary re-renders)
  const currentData = React.useMemo(() => {
    return activeTab === 'clients' ? clientData : tagData;
  }, [activeTab, clientData, tagData]);

  const currentColumns = React.useMemo(() => {
    return activeTab === 'clients' ? clientColumns : tagColumns;
  }, [activeTab, clientColumns, tagColumns]);

  const currentFileName = activeTab === 'clients' ? clientFileName : tagFileName;
  const currentLoading = activeTab === 'clients' ? clientLoading : tagLoading;

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100, // Show 100 rows per page
  });

  // Reset pagination when switching tabs or filtering
  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: 100 });
  }, [activeTab, globalFilter]);

  const table = useReactTable({
    data: currentData,
    columns: currentColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: (row, columnId, filterValue) => {
      const search = filterValue.toLowerCase();
      return Object.values(row.original).some(value => 
        String(value).toLowerCase().includes(search)
      );
    },
  });

  return (
    <div className="excel-spreadsheet-container">
      <div className="spreadsheet-header">
        <div className="header-left">
          <h3>Excel Spreadsheet</h3>
          {currentFileName && (
            <span className="file-name">📄 {currentFileName}</span>
          )}
        </div>
        <div className="header-actions">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
          />
          <button
            className="btn-upload-excel"
            onClick={() => fileInputRef.current?.click()}
            disabled={currentLoading}
          >
            {currentLoading ? 'Loading...' : currentData.length > 0 ? 'Replace File' : 'Upload Excel File'}
          </button>
        </div>
      </div>


      {currentData.length > 0 && (
        <>
          <div className="spreadsheet-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search spreadsheet..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="search-input"
              />
              {globalFilter && (
                <button
                  className="btn-clear-search"
                  onClick={() => setGlobalFilter('')}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <div className="spreadsheet-info">
              Showing {table.getRowModel().rows.length} of {table.getFilteredRowModel().rows.length} rows
              {table.getFilteredRowModel().rows.length !== currentData.length && (
                <span className="search-indicator"> (filtered from {currentData.length.toLocaleString()})</span>
              )}
              {!globalFilter && currentData.length > 100 && (
                <span className="pagination-indicator"> (page {pagination.pageIndex + 1} of {table.getPageCount()})</span>
              )}
            </div>
          </div>

          <div className="spreadsheet-grid">
            <table className="excel-table">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className={header.column.getCanSort() ? 'sortable' : ''}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="header-content">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="sort-indicator">
                              {{
                                asc: ' ↑',
                                desc: ' ↓',
                              }[header.column.getIsSorted()] ?? ' ⇅'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {table.getPageCount() > 1 && (
            <div className="pagination-controls">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="btn-pagination"
              >
                {'<<'}
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="btn-pagination"
              >
                {'<'}
              </button>
              <span className="pagination-info">
                Page {pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="btn-pagination"
              >
                {'>'}
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="btn-pagination"
              >
                {'>>'}
              </button>
              <select
                value={pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
                className="page-size-select"
              >
                {[50, 100, 200, 500, 1000].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      {currentData.length === 0 && currentLoading && (
        <div className="excel-spreadsheet-loading">
          <div className="loading-spinner" />
          <p>Loading spreadsheet data...</p>
        </div>
      )}
      {currentData.length === 0 && !currentLoading && (
        <div className="empty-state">
          <p>📊 Upload an Excel file to get started</p>
          <p className="empty-subtitle">Supports .xlsx, .xls, and .csv files</p>
        </div>
      )}
    </div>
  );
}
